import { useLocation, useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import usePaginate from "@/components/paginate/usePaginate";
import { useState, useEffect } from "react";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { GetSnowflakeJobRunItem, WorkflowType } from "@/pages/query/types";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobRuns } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getWorkflowNavigationPrefix } from "@/pages/query/snowflake/utils";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import {
  useWorkloadRunListParams,
  RUN_STATUS_OPTIONS,
} from "@/pages/query/hooks/useWorkloadRunListParams";

export const RunHistory = () => {
  const { workflowId } = useParams();
  const { currency } = useAppState();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { startDate, endDate } = useDateFromSearchParams();
  const location = useLocation();

  const { sortKey, sortOrder, runStatusFilter, handleRunStatusChange } =
    useWorkloadRunListParams(setPage);

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.TABLEAU
    : WorkflowType.CUSTOM;

  const { data: runsData, isLoading: isRunsLoading } = useQuery({
    queryKey: [
      "snowflake_jobs",
      "runs",
      workflowId,
      startDate,
      endDate,
      page,
      pageSize,
      sortKey,
      sortOrder,
      runStatusFilter,
    ],
    queryFn: () =>
      getSnowflakeJobRuns(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
        sort_key: sortKey,
        sort_order: sortOrder,
        run_status: runStatusFilter,
      }),
    staleTime: 500,
    enabled: Boolean(workflowId),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (runsData) {
      setTotalItems(runsData?.total || 0);
      setTotalPages(runsData?.pages || 0);
    }
  }, [runsData]);

  const tableHeaders: TableHeaderType<GetSnowflakeJobRunItem>[] = [
    {
      id: 1,
      label: "Run ID",
      uid: "run_id",
      render: (item: GetSnowflakeJobRunItem) => (
        <div className="al-text-brand al-break-all cursor-pointer">{item.run_id}</div>
      ),
    },
    {
      id: 2,
      label: "Run Cost",
      uid: "query_cost",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
        sortParamValue: "run_cost",
      },
      render: (item: GetSnowflakeJobRunItem) => <div>{formatCost(item.query_cost, currency)}</div>,
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: GetSnowflakeJobRunItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
    },
    {
      id: 4,
      label: "Start Time",
      uid: "start_time",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: GetSnowflakeJobRunItem) => (
        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
    },
    {
      id: 5,
      label: "Execution Time",
      uid: "execution_time",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: GetSnowflakeJobRunItem) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },
    {
      id: 6,
      label: "Run status",
      uid: "task_status",
      filter: {
        filterType: FilterType.DROPDOWN_SINGLE,
        searchKey: "run_status",
        options: RUN_STATUS_OPTIONS,
        label: "Run Status",
        value: runStatusFilter,
        onChange: handleRunStatusChange,
      },
      render: (item: GetSnowflakeJobRunItem) => (
        <ExecutionStatusCount run_count={item.total_count} success_count={item.success_count} />
      ),
    },
  ];

  return (
    <Stack direction="column" className={"al-mt-4"}>
      <DataTable
        isLoading={isRunsLoading}
        tableClass="mt-0"
        getRowUrl={item =>
          `${getWorkflowNavigationPrefix(workflowType)}/${encodeURIComponent(workflowId!)}/${item.run_id}`
        }
        getRowState={item => ({ entityName: item.run_id })}
        header={tableHeaders}
        data={{
          items: runsData?.items ?? [],
        }}
        idKey={(item: GetSnowflakeJobRunItem) => item.run_id}
        resetPage={() => setPage(0)}
        defaultSort={{ sortParamValue: "start_time", order: "desc" }}
      />
      <Paginate
        itemCount={totalItems}
        numPages={totalPages}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};
